import { ArrowSquareOut } from '@liveauctioneers/hammer-ui-core/icons';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, Caption, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback } from 'react';

import { ChairishItem } from '@/redux/modules/chairishSimilarItems/chairishSimilarItems.types';
import {
    LotRecommendationTrigger,
    LotRecommendationType,
    useTrackLotRecommendationClickedAnalytics,
} from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

const UTM_PARAMS = 'utm_source=LiveAuctioneers&utm_medium=referral&utm_campaign=la_zrp_chairish_similar_items';

type ChairishItemCardProps = {
    item: ChairishItem;
    position: number;
};

export const ChairishItemCard = ({ item, position }: ChairishItemCardProps) => {
    const formatMessage = useAppFormatMessage();
    const { formatNumber } = useIntl();
    const trackClick = useTrackLotRecommendationClickedAnalytics();

    const href = `${item.lotUrl}${item.lotUrl.includes('?') ? '&' : '?'}${UTM_PARAMS}`;
    const price =
        item.startPrice > 0
            ? formatNumber(item.startPrice, {
                  currency: 'USD',
                  style: 'currency',
              })
            : null;

    const handleClick = useCallback(() => {
        trackClick({
            catalogId: 0,
            houseId: 0,
            itemId: Number(item.lotId) || 0,
            lotClickPosition: position + 1,
            recommendationName: LotRecommendationType.AvailableOnChairish,
            trigger: LotRecommendationTrigger.ImageClick,
        });
    }, [trackClick, item.lotId, position]);

    return (
        <a
            className="group flex h-full w-[340px] flex-col overflow-hidden rounded-md border border-border-secondary bg-background-primary smMax:w-[calc((100vw-48px)/2)]"
            href={href}
            onClick={handleClick}
            rel="noopener noreferrer"
            target="_blank"
        >
            <div className="relative aspect-square w-full shrink-0 overflow-hidden">
                <img
                    alt={item.title}
                    className="absolute inset-0 size-full object-contain"
                    loading="lazy"
                    src={item.imageUrl}
                />
            </div>
            <div className="flex grow flex-col justify-between px-sm py-xs">
                <BodyPrimary className="line-clamp-2 min-h-12 text-link-monochrome">{item.title}</BodyPrimary>
                <div className="flex items-center justify-between">
                    {price && <LabelStrong className="text-text-primary">{price}</LabelStrong>}
                    <Caption className="flex shrink-0 items-center gap-2xs text-link-primary opacity-100 transition-opacity smMin:opacity-0 smMin:group-hover:opacity-100">
                        <span className="smMax:hidden">
                            {formatMessage(['searchPage', 'zeroResults', 'chairish', 'viewOn'])}
                        </span>
                        <ArrowSquareOut size="xs" />
                    </Caption>
                </div>
            </div>
        </a>
    );
};
